import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromAssistants from '../../assistants';
import { chatsFeature, ChatsState } from '../../store';

const CONVERSATION_URL = '/chats/conversation';
const ASSISTANTS_URL = '/chats/assistants';
const TEAMMATES_URL = '/chats/teammates';

/**
 * Service that standardizes navigation patterns throughout the application.
 * All router.navigate and routerLink usages should go through this service.
 */
@Injectable({
  providedIn: 'root',
})
export class RouterService {
  private readonly router = inject(Router);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly assistantsStore =
    inject<Store<fromAssistants.AssistantsState>>(Store);

  // ============================================================================
  // Conversation Routes
  // ============================================================================

  /**
   * Navigate to start a new conversation (without assistant).
   * Looks up the existing empty chat in the store and navigates to it.
   * Falls back to base URL if no empty chat exists yet (URL sync service
   * will then handle creating/selecting one).
   */
  public async navigateToEmptyConversation(): Promise<boolean> {
    const emptyChatId = this.getEmptyChatId();

    if (emptyChatId) {
      return this.navigateToConversationWithChat(emptyChatId);
    }

    // Fallback: navigate to base URL, URL sync service will handle selection
    return await this.router.navigate([CONVERSATION_URL], {
      queryParams: { assistantUuid: null },
    });
  }

  /**
   * Navigate to start a new conversation with a specific assistant.
   * Validates the assistant exists before navigation; redirects to 404 if not.
   * Looks up the existing empty chat in the store and navigates to it
   * with the assistant UUID. Falls back to base URL if no empty chat
   * exists yet (URL sync service will then handle creating/selecting one).
   */
  public async navigateToEmptyConversationWithAssistant(
    assistantUuid: string,
  ): Promise<boolean> {
    // Validate assistant exists before navigation
    if (!this.isAssistantValid(assistantUuid)) {
      return this.navigateToNotFound();
    }

    const emptyChatId = this.getEmptyChatId();

    if (emptyChatId) {
      return this.navigateToConversationWithChatAndAssistant(
        emptyChatId,
        assistantUuid,
      );
    }

    // Fallback: navigate to base URL without chatId; URL sync service will handle selection
    return await this.router.navigate([CONVERSATION_URL], {
      queryParams: { assistantUuid },
    });
  }

  /**
   * Navigate to a conversation with a specific chat ID.
   * Route: /chats/conversation/:id
   */
  public async navigateToConversationWithChat(
    chatId: number,
    navigationExtras?: Pick<NavigationExtras, 'replaceUrl'>,
  ): Promise<boolean> {
    return await this.router.navigate([CONVERSATION_URL, chatId], {
      ...(navigationExtras?.replaceUrl ? { replaceUrl: true } : {}),
      queryParams: {
        assistantUuid: null,
      },
    });
  }

  /**
   * Navigate to a conversation with both chat ID and assistant UUID.
   * Route: /chats/conversation/:id?assistantUuid=:uuid
   */
  public async navigateToConversationWithChatAndAssistant(
    chatId: number,
    assistantUuid: string,
    navigationExtras?: Pick<NavigationExtras, 'replaceUrl'>,
  ): Promise<boolean> {
    return await this.router.navigate([CONVERSATION_URL, chatId], {
      ...(navigationExtras?.replaceUrl ? { replaceUrl: true } : {}),
      queryParams: {
        assistantUuid,
      },
    });
  }

  // ============================================================================
  // Assistants Routes
  // ============================================================================

  /**
   * Navigate to the assistants page.
   * Route: /chats/assistants
   * Optional: /chats/assistants?tab=:tab
   */
  public async navigateToAssistants(
    tab?: fromAssistants.AssistantsPageTab,
  ): Promise<boolean> {
    const queryParams = tab ? { tab } : null;
    return await this.router.navigate([ASSISTANTS_URL], {
      queryParams,
    });
  }

  // ============================================================================
  // Teammates Routes
  // ============================================================================

  /**
   * Navigate to a specific teammate page.
   * Route: /chats/teammates/:slug
   */
  public async navigateToTeammate(slug: string): Promise<boolean> {
    return await this.router.navigate([TEAMMATES_URL, slug]);
  }

  // ============================================================================
  // Landing
  // ============================================================================

  /**
   * Navigate to the app's authenticated landing page — the teammates list, the
   * default `/chats` redirect target. Used to send a user somewhere safe when a
   * resource they were viewing is revoked out from under them.
   */
  public async navigateToLanding(): Promise<boolean> {
    return await this.router.navigate([TEAMMATES_URL]);
  }

  // ============================================================================
  // Not Found Routes
  // ============================================================================

  /**
   * Navigate to the not found page.
   * Route: /not-found
   */
  public async navigateToNotFound(): Promise<boolean> {
    return await this.router.navigate(['/not-found'], {
      replaceUrl: true,
    });
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  /**
   * Gets the ID of an empty chat (no messages) from the store.
   * Returns null if no empty chat exists.
   */
  private getEmptyChatId(): number | null {
    const chats = this.chatsStore.selectSignal(chatsFeature.selectAll)();
    const emptyChat = chats.find((chat) => chat._count.messages === 0);
    return emptyChat?.id ?? null;
  }

  /**
   * Checks if the given assistant UUID exists in the store.
   */
  private isAssistantValid(assistantUuid: string): boolean {
    return !!this.assistantsStore.selectSignal(
      fromAssistants.assistantsFeature.selectEntities,
    )()[assistantUuid];
  }
}
